import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import Button from "@ats/src/components/shared/Button";
import FeaturePromoFrame from "@ats/src/components/shared/FeaturePromoFrame";

interface Props {
  children: React.ReactNode;
  eyebrow?: string;
  titleAccent?: string;
  title: string;
  description: string;
  ctaLabel?: string;
  ctaHref?: string;
}

export default function FeaturePromoPanel(props: Props) {
  const { children, eyebrow, titleAccent, title, description, ctaLabel, ctaHref } = props;

  return (
    <Styled.Panel>
      <Styled.Card>
        <Styled.Content>
          <FeaturePromoFrame>{children}</FeaturePromoFrame>

          <Styled.Copy>
            {eyebrow != undefined ? <Styled.Eyebrow>{eyebrow}</Styled.Eyebrow> : null}
            <Styled.Title>
              {titleAccent != undefined ? (
                <Styled.TitleAccent>{titleAccent} </Styled.TitleAccent>
              ) : null}
              {title}
            </Styled.Title>
            <Styled.Description>{description}</Styled.Description>
            {ctaLabel != undefined && ctaHref != undefined ? (
              <Styled.Actions>
                <Styled.Cta>
                  <Button styleType="primary" type="externalLink" link={ctaHref}>
                    {ctaLabel}
                  </Button>
                </Styled.Cta>
              </Styled.Actions>
            ) : null}
          </Styled.Copy>
        </Styled.Content>
      </Styled.Card>
    </Styled.Panel>
  );
}

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Panel = styled.div((props: any) => {
  const t: any = props.theme;

  return css`
    label: FeaturePromoPanel;
    position: relative;
    display: flex;
    overflow: hidden;
    width: 100%;
    height: 100%;
    min-height: 400px;
    box-sizing: border-box;
    padding: 12px;
    background-color: ${t.dark ? "#0a0a0a" : t.color.white};

    ${t.mq["xxl"]} {
      padding: 14px;
    }
  `;
});

Styled.Card = styled.div((props: any) => {
  const t: any = props.theme;

  return css`
    label: FeaturePromoPanel_Card;
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    border-radius: 0.8125rem;
    background: ${t.color.gray[800]};
  `;
});

Styled.Content = styled.div((props: any) => {
  const t: any = props.theme;

  return css`
    label: FeaturePromoPanel_Content;
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    min-height: 0;
    box-sizing: border-box;
    padding: 24px;

    ${t.mq["xxl"]} {
      padding: 34px;
    }
  `;
});

Styled.Cta = styled.div((props: any) => {
  const t: any = props.theme;

  return css`
    label: FeaturePromoPanel_Cta;
    display: inline-flex;

    a,
    a:link,
    a:visited {
      background: linear-gradient(120deg, #fbd7ff 10%, #ffdec1 90%);
      color: ${t.color.black};
    }

    a:hover:not([disabled]) {
      background: linear-gradient(100deg, #fbd7ff 10%, #ffdec1 90%);
      color: ${t.color.black};
    }
  `;
});

Styled.Copy = styled.div((props: any) => {
  const t: any = props.theme;

  return css`
    label: FeaturePromoPanel_Copy;
    width: 100%;
    max-width: 624px;
    flex-shrink: 0;
    margin-top: ${t.spacing[9]};
  `;
});

Styled.Eyebrow = styled.div((props: any) => {
  const t: any = props.theme;

  return css`
    label: FeaturePromoPanel_Eyebrow;
    ${t.text.bold}
    font-size: 0.78125rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #e7b9d8;
  `;
});

Styled.Title = styled.h2((props: any) => {
  const t: any = props.theme;

  return css`
    label: FeaturePromoPanel_Title;
    margin: ${t.spacing[3]} 0 0;
    ${t.text.bold}
    font-size: 2.5rem;
    line-height: 1.08;
    letter-spacing: -0.02em;
    color: ${t.color.white};
  `;
});

Styled.TitleAccent = styled.span`
  label: FeaturePromoPanel_TitleAccent;
  background: linear-gradient(84.47deg, #fbd7ff 0%, #ffe4cc 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

Styled.Description = styled.p((props: any) => {
  const t: any = props.theme;

  return css`
    label: FeaturePromoPanel_Description;
    margin: ${t.spacing[3]} 0 0;
    max-width: 440px;
    font-size: 0.96875rem;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.64);
    text-wrap: pretty;
  `;
});

Styled.Actions = styled.div((props: any) => {
  const t: any = props.theme;

  return css`
    label: FeaturePromoPanel_Actions;
    display: flex;
    margin-top: ${t.spacing[6]};
  `;
});
